import { BrandedProductId, PremiumProductId } from '@seek/hirer-products-shim';
import { useTranslations } from '@vocab/react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual, batch } from 'react-redux';

import { TEALIUM_AD_TYPE } from 'src/app/constants/dispatcherId';
import getTrackingAdapter from 'src/app/dataAnalytics';
import {
  PAGE_PAY_AND_POST,
  PREMIUM_UPSELL_DISPLAYED,
  PREMIUM_UPSELL_PRESSED,
} from 'src/app/dataAnalytics/schema/constants';
import tealiumDispatch from 'src/app/state/dispatcher/tealium';
import { actionUpdateSelectedProductId } from 'src/app/state/modules/formData/selectedProductId';
import { actionResetAdBudget } from 'src/app/state/modules/session/adBudget';
import { actionUpdateBuyUpSell } from 'src/app/state/modules/session/buyUpSell';
import { getSelectedProduct } from 'src/app/utils/getSelectedProduct';
import { log } from 'src/app/utils/logging';
import { usePremiumUpgradePrice } from 'src/app/views/hooks/usePremiumUpgradePrice';
import { UpsellPanel } from 'src/app/views/shared/UpsellPanel';
import type AppState from 'src/types/appState';

import translations from './.vocab';
import upsellImage from './premium-upsell.png';

import * as styles from './index.css';

export const PremiumUpsell = () => {
  const { t } = useTranslations(translations);

  const { currentSelectedProductId, isPaymentInProgress, productSet, page } =
    useSelector(
      (state: AppState) => ({
        currentSelectedProductId: state.formData.selectedProductId,
        isPaymentInProgress: state.session.isPaymentInProgress,
        productSet: state.session.productSet,
        page: state.tealium.page,
      }),
      shallowEqual,
    );

  const [componentReady, setComponentReady] = useState(false);
  const { price, tax } = usePremiumUpgradePrice();
  const dispatch = useDispatch();

  // TODO: Clean up - Remove this once we go live with new tealium event tracker
  useEffect(() => {
    if (page === PAGE_PAY_AND_POST) {
      setComponentReady(true);
    }
  }, [page]);

  // TODO: Clean up - Simplify this once we go live with new tealium event tracker
  useEffect(() => {
    if (componentReady) {
      const eventData = {
        eventName: PREMIUM_UPSELL_DISPLAYED,
        adBudgetType: 'premiumUpsell',
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    }
  }, [componentReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = () => {
    if (isPaymentInProgress) {
      return;
    }

    const nextProductId =
      currentSelectedProductId === PremiumProductId
        ? BrandedProductId
        : PremiumProductId;

    const selectedProduct = getSelectedProduct(nextProductId, productSet);
    if (selectedProduct && productSet) {
      const eventData = {
        eventName: PREMIUM_UPSELL_PRESSED,
        linkAction: nextProductId === PremiumProductId ? 'select' : 'remove',
        premiumUpgradePrice: price,
      };
      getTrackingAdapter.sendLinkEvent(eventData);

      batch(() => {
        dispatch(actionResetAdBudget());
        dispatch(actionUpdateSelectedProductId(selectedProduct.id));
        dispatch(tealiumDispatch(TEALIUM_AD_TYPE, selectedProduct.name));
        dispatch(actionUpdateBuyUpSell(nextProductId === PremiumProductId));
      });
    } else {
      log.warn(
        'PremiumUpsell | handleClick | selected product not found in product set',
        {
          selectedProductId: nextProductId,
          productSet,
          currentSelectedProductId,
        },
      );
    }
  };

  return (
    <UpsellPanel
      price={price}
      tax={tax}
      heading={t('premium-upsell-heading')}
      priceDescription={t('premium-upsell-price-description')}
      benefits={[
        t('premium-upsell-benefits-1'),
        t('premium-upsell-benefits-2'),
      ]}
      image={upsellImage}
      imageClassName={styles.image}
      onClick={handleClick}
      isSelected={currentSelectedProductId === PremiumProductId}
    />
  );
};
